import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Button,
  Input,
  FormControl,
  FormLabel,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getCampaignTicketTimeline } from '../../../Http/Index';
import CompanySelector from '../../../GlobalComponent/companySelector/CompanySelector';
import moment from 'moment';

interface TimelineDataPoint {
  date: string;
  category: string;
  value: number;
}

interface ChartDataPoint {
  date: string;
  'Email Campaigns': number;
  'WhatsApp Campaigns': number;
  'Tickets Raised': number;
}

interface TimelineSummary {
  totalWhatsApp: number;
  totalEmail: number;
  totalTickets: number;
}

export const CampaignTimeline: React.FC = () => {
  const [data, setData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(
    moment().subtract(14, 'days').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>('');
  const [summary, setSummary] = useState<TimelineSummary>({
    totalWhatsApp: 0,
    totalEmail: 0,
    totalTickets: 0,
  });

  const cardBg = useColorModeValue('white', 'gray.700');

  const transformDataForRecharts = (rawData: TimelineDataPoint[]): ChartDataPoint[] => {
    // Group data by date
    const groupedByDate: Record<string, ChartDataPoint> = {};

    rawData.forEach((item) => {
      if (!groupedByDate[item.date]) {
        groupedByDate[item.date] = {
          date: item.date,
          'Email Campaigns': 0,
          'WhatsApp Campaigns': 0,
          'Tickets Raised': 0,
        };
      }
      if (item.category === 'Email Campaigns') {
        groupedByDate[item.date]['Email Campaigns'] = item.value;
      } else if (item.category === 'WhatsApp Campaigns') {
        groupedByDate[item.date]['WhatsApp Campaigns'] = item.value;
      } else if (item.category === 'Tickets Raised') {
        groupedByDate[item.date]['Tickets Raised'] = item.value;
      }
    });

    // Sort by date
    return Object.values(groupedByDate).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  };

  const fetchTimelineData = useCallback(async () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }

    // Validate date range is not more than 90 days
    const start = moment(startDate);
    const end = moment(endDate);
    const daysDiff = end.diff(start, 'days');

    if (daysDiff < 0) {
      setError('End date must be after start date');
      return;
    }

    if (daysDiff > 90) {
      setError('Date range cannot exceed 90 days. Please select a shorter range.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params: { startDate: string; endDate: string; companyId?: string } = {
        startDate,
        endDate,
      };
      
      // Add companyId filter if a company is selected
      if (selectedCompanyId) {
        params.companyId = selectedCompanyId;
      }

      const response = await getCampaignTicketTimeline(params);

      if (response.data?.data) {
        const timelineData: TimelineDataPoint[] = response.data.data;
        const chartData = transformDataForRecharts(timelineData);
        setData(chartData);

        // Calculate summary
        const summaryData = timelineData.reduce(
          (acc, item) => {
            if (item.category === 'WhatsApp Campaigns') {
              acc.totalWhatsApp += item.value;
            } else if (item.category === 'Email Campaigns') {
              acc.totalEmail += item.value;
            } else if (item.category === 'Tickets Raised') {
              acc.totalTickets += item.value;
            }
            return acc;
          },
          { totalWhatsApp: 0, totalEmail: 0, totalTickets: 0 }
        );
        setSummary(summaryData);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch timeline data');
      console.error('Error fetching timeline data:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedCompanyId]);

  useEffect(() => {
    fetchTimelineData();
  }, [fetchTimelineData]);

  const handleQuickSelect = (days: number) => {
    setStartDate(moment().subtract(days, 'days').format('YYYY-MM-DD'));
    setEndDate(moment().format('YYYY-MM-DD'));
  };

  const formatDate = (dateStr: string) => {
    return moment(dateStr).format('MMM DD');
  };

  return (
    <Box p={4}>
      {/* Header */}
      <Text fontSize="xl" fontWeight="bold" mb={4}>
        Campaign Sends vs Tickets Raised
      </Text>

      {/* Date Filters */}
      <Card bg={cardBg} mb={4} shadow="sm">
        <CardBody>
          <Flex gap={4} align="flex-end" wrap="wrap">
            <FormControl w="180px">
              <FormLabel fontSize="sm">Start Date</FormLabel>
              <Input
                type="date"
                size="sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </FormControl>

            <FormControl w="180px">
              <FormLabel fontSize="sm">End Date</FormLabel>
              <Input
                type="date"
                size="sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </FormControl>

            {/* Company Filter */}
            <FormControl w="250px">
              <FormLabel fontSize="sm">Company</FormLabel>
              <Flex gap={2} align="center">
                <Box flex="1">
                  <CompanySelector
                    selectedCompany={selectedCompanyName}
                    onCompanySelect={(companyId, companyName) => {
                      setSelectedCompanyId(companyId);
                      setSelectedCompanyName(companyName);
                    }}
                    label=""
                    width="100%"
                    placeholder="All Companies"
                  />
                </Box>
                {selectedCompanyId && (
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => {
                      setSelectedCompanyId('');
                      setSelectedCompanyName('');
                    }}
                    title="Clear company filter"
                  >
                    ✕
                  </Button>
                )}
              </Flex>
            </FormControl>

            <Button
              size="sm"
              colorScheme="blue"
              onClick={fetchTimelineData}
              isLoading={loading}
            >
              Apply
            </Button>

            <Button
              size="sm"
              variant="outline"
              onClick={fetchTimelineData}
              isLoading={loading}
            >
              Refresh
            </Button>

            {/* Quick Select Buttons */}
            <Flex gap={2} ml={{ md: 'auto' }}>
              <Button size="sm" variant="ghost" onClick={() => handleQuickSelect(7)}>
                Last 7 Days
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleQuickSelect(14)}>
                Last 14 Days
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleQuickSelect(30)}>
                Last 30 Days
              </Button>
              <Button size="sm" variant="ghost" onClick={() => handleQuickSelect(90)}>
                Last 90 Days
              </Button>
            </Flex>
          </Flex>
        </CardBody>
      </Card>

      {/* Summary Stats */}
      <Card bg={cardBg} mb={4} shadow="sm">
        <CardBody>
          <StatGroup>
            <Stat>
              <StatLabel>Total Email Sends</StatLabel>
              <StatNumber color="blue.500">
                {summary.totalEmail.toLocaleString()}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Total WhatsApp Sends</StatLabel>
              <StatNumber color="orange.500">
                {summary.totalWhatsApp.toLocaleString()}
              </StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Total Tickets Raised</StatLabel>
              <StatNumber color="green.500">
                {summary.totalTickets.toLocaleString()}
              </StatNumber>
            </Stat>
          </StatGroup>
        </CardBody>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Flex justify="center" py={10}>
          <Spinner size="lg" />
        </Flex>
      )}

      {/* Chart */}
      {!loading && data.length > 0 && (
        <Card bg={cardBg} shadow="sm">
          <CardBody>
            <Text fontSize="md" fontWeight="medium" mb={2} color="gray.600">
              Count by Date
            </Text>
            <Box h="400px">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    labelFormatter={(label) => {
                      // Parse the label (e.g., "Sep 30") with current year
                      const currentYear = moment().year();
                      return moment(`${label} ${currentYear}`, 'MMM D YYYY').format('MMM DD, YYYY');
                    }}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="Email Campaigns"
                    stroke="#1890ff"
                    strokeWidth={2}
                    dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="WhatsApp Campaigns"
                    stroke="#fa8c16"
                    strokeWidth={2}
                    dot={{ fill: '#fa8c16', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Tickets Raised"
                    stroke="#52c41a"
                    strokeWidth={2}
                    dot={{ fill: '#52c41a', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardBody>
        </Card>
      )}

      {/* Empty State */}
      {!loading && data.length === 0 && !error && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          No data available for the selected date range. Try selecting a different range.
        </Alert>
      )}
    </Box>
  );
};

export default CampaignTimeline;
